/* InstructorLessons — embedded inside the InstructorDashboard.
 * Lets instructors:
 *   - Submit new lesson content for admin review (status=pending)
 *   - Re-edit their pending or rejected submissions
 *   - See their published / pending / rejected contributions in one place */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, X, Send, Pencil, Trash2, BookOpen, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import FileDropField from '../admin/FileDropField';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

const RESOURCE_KINDS = ['link', 'video', 'pdf', 'slides', 'doc'];

function StatusBadge({ status }) {
  if (status === 'published') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 className="w-3 h-3" /> Published
    </span>
  );
  if (status === 'pending') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
      <Clock className="w-3 h-3" /> Pending review
    </span>
  );
  if (status === 'rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">
      <AlertCircle className="w-3 h-3" /> Needs revision
    </span>
  );
  return null;
}

export default function InstructorLessons({ programs }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/academy/instructor/lessons');
      setRows(data.rows || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const remove = async (l) => {
    if (l.status === 'published') {
      alert('Published lessons can\'t be deleted by instructors. Contact an admin.');
      return;
    }
    if (!window.confirm(`Delete "${l.title}"?`)) return;
    await api.delete(`/api/academy/instructor/lessons/${l.id}`);
    reload();
  };

  const counts = useMemo(() => {
    const c = { published: 0, pending: 0, rejected: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <section className="rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white mt-8" data-testid="instructor-lessons">
      <header className="bg-[#0e0e10] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#f5a623]" />
          <span className="text-[14px] font-bold tracking-[0.18em] uppercase">My course content</span>
          <span className="text-[11px] text-white/60 ml-2">
            {counts.published} published · {counts.pending} pending · {counts.rejected} need revision
          </span>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="instructor-new-lesson-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider text-[#0e0e10]"
          style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
          <Plus className="w-3.5 h-3.5" /> Submit a lesson
        </button>
      </header>

      <div className="p-5">
        {loading ? (
          <div className="text-[#6E6E73] text-[13px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-[#FAFAFA] p-8 text-center text-[#6E6E73]">
            <BookOpen className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
            <div className="text-[14px] font-bold text-[#1D1D1F] mb-1">You haven't submitted any lessons yet</div>
            <p className="text-[12.5px]">Share teaching material with your students. An admin reviews each submission before students see it.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {rows.map((l) => (
              <li key={l.id} className="rounded-xl border border-[#E5E5E7] bg-white p-4" data-testid={`instructor-lesson-${l.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-[14px] font-extrabold text-[#0e0e10]">{l.title}</h3>
                      <StatusBadge status={l.status} />
                    </div>
                    <div className="text-[11px] text-[#6E6E73] mb-1">
                      {(programs || []).find((p) => p.slug === l.program_slug)?.name || l.program_slug}
                      {l.module_title ? ` · ${l.module_title}` : ''}
                      {' · updated '}{new Date(l.updated_at).toLocaleDateString()}
                    </div>
                    {l.body_md && (
                      <p className="text-[12.5px] text-[#3a3a3f] line-clamp-2 whitespace-pre-wrap mt-1">{l.body_md}</p>
                    )}
                    {l.status === 'rejected' && l.rejection_reason && (
                      <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-[12px] text-red-700">
                        <strong>Reviewer feedback:</strong> {l.rejection_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {l.status !== 'published' && (
                      <button onClick={() => { setEditing(l); setShowForm(true); }}
                        data-testid={`instructor-lesson-edit-${l.id}`}
                        className="inline-flex items-center gap-1 text-[11.5px] text-[#c9a227] hover:underline font-semibold">
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                    )}
                    {l.status !== 'published' && (
                      <button onClick={() => remove(l)}
                        data-testid={`instructor-lesson-delete-${l.id}`}
                        className="inline-flex items-center gap-1 text-[11.5px] text-red-600 hover:underline">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showForm && (
        <SubmitLessonModal
          lesson={editing}
          programs={programs}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); reload(); }}
        />
      )}
    </section>
  );
}

function SubmitLessonModal({ lesson, programs, onClose, onSaved }) {
  const editing = !!lesson;
  const [programSlug, setProgramSlug] = useState(lesson?.program_slug || (programs?.[0]?.slug ?? ''));
  const [moduleTitle, setModuleTitle] = useState(lesson?.module_title || '');
  const [title, setTitle] = useState(lesson?.title || '');
  const [body, setBody] = useState(lesson?.body_md || '');
  const [resources, setResources] = useState(lesson?.resources || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setBusy(true); setError('');
    try {
      const payload = {
        program_slug: programSlug,
        module_title: moduleTitle.trim() || null,
        title: title.trim(),
        body_md: body,
        resources: resources.filter((r) => r.url.trim()),
      };
      if (editing) {
        await api.patch(`/api/academy/instructor/lessons/${lesson.id}`, payload);
      } else {
        await api.post(`/api/academy/instructor/lessons`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-start justify-center px-4 py-8 overflow-auto" data-testid="instructor-lesson-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[640px] w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between bg-gradient-to-br from-[#1a1a1f] to-[#0e0e10] text-white">
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#f5a623]">{editing ? 'Edit submission' : 'New lesson'}</div>
            <div className="text-[16px] font-extrabold">Share teaching content</div>
          </div>
          <button onClick={onClose} data-testid="instructor-lesson-close" className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-3">
          <p className="text-[12.5px] text-[#6E6E73]">
            Submissions go to the admin queue for review. You'll see "{editing ? 'pending review' : 'pending'}" here once submitted —
            students see your lesson on the course page only after approval.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Program</label>
              <select value={programSlug} onChange={(e) => setProgramSlug(e.target.value)}
                disabled={editing}
                data-testid="instructor-lesson-program"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px] disabled:bg-[#f5f5f7]">
                {(programs || []).map((p) => (
                  <option key={p.slug} value={p.slug}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Module (optional)</label>
              <input value={moduleTitle} onChange={(e) => setModuleTitle(e.target.value)}
                data-testid="instructor-lesson-module"
                placeholder="Module 1: Foundations"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required
              data-testid="instructor-lesson-title"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[14px]" />
          </div>

          <div>
            <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Body (Markdown OK)</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9}
              data-testid="instructor-lesson-body"
              placeholder="Walk students through this lesson…"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px] font-mono" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73]">Resources (videos, PDFs, links)</label>
              <button type="button"
                data-testid="instructor-lesson-add-resource"
                onClick={() => setResources((r) => [...r, { label: '', url: '', kind: 'link' }])}
                className="inline-flex items-center gap-1 text-[11.5px] text-[#c9a227] hover:underline font-semibold">
                <Plus className="w-3 h-3" /> Add link
              </button>
            </div>
            <FileDropField
              onUploaded={(res) => setResources((rs) => [...rs, { label: res.label, url: res.url, kind: res.kind }])}
            />
            <div className="space-y-1.5 mt-2">
              {resources.map((r, i) => (
                <div key={i} className="flex items-stretch gap-2">
                  <input value={r.label} onChange={(e) => setResources((rs) => rs.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                    placeholder="Label" className="w-[150px] px-2 py-1.5 rounded-lg border border-[#E5E5E7] outline-none focus:border-[#c9a227] text-[12.5px]" />
                  <input value={r.url} onChange={(e) => setResources((rs) => rs.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                    placeholder="https://…" className="flex-1 px-2 py-1.5 rounded-lg border border-[#E5E5E7] outline-none focus:border-[#c9a227] text-[12.5px]" />
                  <select value={r.kind} onChange={(e) => setResources((rs) => rs.map((x, j) => j === i ? { ...x, kind: e.target.value } : x))}
                    className="px-2 py-1.5 rounded-lg border border-[#E5E5E7] outline-none focus:border-[#c9a227] text-[12.5px]">
                    {RESOURCE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <button type="button" onClick={() => setResources((rs) => rs.filter((_, j) => j !== i))}
                    className="text-red-600 hover:bg-red-50 rounded-lg px-2">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12.5px] px-3 py-2" data-testid="instructor-lesson-error">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-[12.5px] text-[#6E6E73] hover:text-[#0e0e10] px-3">Cancel</button>
            <button type="submit" disabled={busy}
              data-testid="instructor-lesson-submit"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-[12.5px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
              <Send className="w-3.5 h-3.5" /> {busy ? 'Submitting…' : (editing ? 'Save & resubmit' : 'Submit for review')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
